import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Build from '../core/Build';
import GenConfig from '../core/GenConfig';
import * as buildStorage from '../services/buildStorage';
import { pushAnonBuild, retractAllAnonBuilds } from '../services/anonSync';
import { loadItemCatalog } from '../services/itemCatalog';
import { getGenDefaults } from '../services/genDefaults';
import {
    createRemoteBuild,
    deleteRemoteBuild,
    getPublicBuild,
    getRemoteBuild,
    importLocalBuilds,
    listRemoteBuilds,
    updateRemoteBuild,
} from '../services/buildApi';
import { useAuth } from '../contexts/AuthContext';
import { canonicalBuildPath } from '../utils/buildRoutes';

const DEFAULT_ACTIVE_MENU = 'stats';
const KEY_ACTIVE_REMOTE_ID = 'dofgen_active_remote_build_id';
const KEY_IMPORT_DECISION_PREFIX = 'dofgen_import_decision_v1_';
const DEFAULT_REMOTE_BUILD = { lvl: 200, items: Array(16).fill(null), criteriaTags: [] };

function entryToLoadedState(entry) {
    return {
        build: new Build(entry?.build || {}),
        buildName: entry?.name || '',
        genConfig: new GenConfig(entry?.genConfig || {}),
    };
}

function normalizeRemoteEntry(entry) {
    if (!entry) return null;
    return {
        id: entry.id,
        name: entry.name || '',
        build: entry.build || {},
        genConfig: entry.genConfig || {},
        createdAt: entry.createdAt || null,
        updatedAt: entry.updatedAt || null,
        lastOpenedAt: entry.lastOpenedAt || null,
        publicCode: entry.publicCode || '',
        owner: entry.owner || null,
        metrics: entry.metrics || { likes: 0, views: 0 },
        viewer: entry.viewer || {},
        canonicalPath: entry.canonicalPath || null,
        canonicalUrl: entry.canonicalUrl || null,
        scope: 'remote',
    };
}

function localBuildMeta(entry = null) {
    return {
        scope: 'local',
        canEdit: true,
        ownerSlug: null,
        publicCode: null,
        canonicalPath: null,
        owner: null,
        createdAt: entry?.createdAt || null,
        updatedAt: entry?.updatedAt || null,
        metrics: { likes: 0, views: 0 },
    };
}

function remoteBuildMeta(entry, { canEdit, ownerSlug }) {
    return {
        scope: 'remote',
        canEdit: !!canEdit,
        ownerSlug: ownerSlug || entry?.owner?.public_slug || null,
        publicCode: entry?.publicCode || null,
        canonicalPath: entry?.canonicalPath || null,
        owner: entry?.owner || null,
        createdAt: entry?.createdAt || null,
        updatedAt: entry?.updatedAt || null,
        metrics: entry?.metrics || { likes: 0, views: 0 },
        viewer: entry?.viewer || {},
    };
}

function getImportDecisionKey(userId) {
    return `${KEY_IMPORT_DECISION_PREFIX}${userId}`;
}

export const useGeneratorState = () => {
    const { authState, isAuthenticated, user } = useAuth();

    const [builds, setBuilds] = useState(() => buildStorage.getBuilds());
    const [activeBuildId, setActiveBuildIdState] = useState(() => buildStorage.getActiveBuildId());
    const [build, setBuildRaw] = useState(() => {
        const id = buildStorage.getActiveBuildId();
        const entry = id ? buildStorage.getBuild(id) : null;
        if (entry) return entryToLoadedState(entry).build;
        const created = buildStorage.createBuild();
        return entryToLoadedState(created).build;
    });
    const [buildName, setBuildNameRaw] = useState(() => {
        const id = buildStorage.getActiveBuildId();
        const entry = id ? buildStorage.getBuild(id) : null;
        return entry?.name || '';
    });
    const [genConfig, setGenConfigRaw] = useState(() => {
        const id = buildStorage.getActiveBuildId();
        const entry = id ? buildStorage.getBuild(id) : null;
        return entry ? entryToLoadedState(entry).genConfig : new GenConfig();
    });
    const [activeMenu, setActiveMenuRaw] = useState(() => buildStorage.getActiveMenu() || DEFAULT_ACTIVE_MENU);
    // True until the first route bootstrap finishes. The state above is built
    // synchronously from storage, before the item catalog exists, so a build
    // read at that moment is sixteen unresolved ids: real equipment that the
    // page cannot render yet. Showing it is worse than showing nothing —
    // an empty grid and a stat sheet missing every item reads as "my build is
    // gone". Latches false and stays there; later bootstraps are instant
    // because the catalog is already in memory.
    const [isBootstrapping, setIsBootstrapping] = useState(true);
    const [storageMode, setStorageMode] = useState('local');
    const [currentBuildMeta, setCurrentBuildMeta] = useState(localBuildMeta);

    const currentBuildMetaRef = useRef(currentBuildMeta);
    const remoteSaveTimerRef = useRef(null);
    const pendingRemotePatchRef = useRef({});
    currentBuildMetaRef.current = currentBuildMeta;

    // Always points at the latest committed build. Lets updateBuild accept an
    // updater function so async callbacks (e.g. the set-bonus self-heal fetch in
    // BuildSetsPanel) compose against current state instead of a stale closure.
    const buildRef = useRef(build);
    buildRef.current = build;

    const persistLocalEntry = useCallback((id, updates) => {
        if (!id) return;
        const next = buildStorage.updateBuild(id, updates);
        setBuilds(buildStorage.getBuilds());
        if (
            next?.updatedAt
            && currentBuildMetaRef.current.scope === 'local'
            && id === activeBuildId
        ) {
            setCurrentBuildMeta(prev => ({
                ...prev,
                updatedAt: next.updatedAt,
                createdAt: prev.createdAt || next.createdAt || null,
            }));
        }
    }, [activeBuildId]);

    const updateRemoteListEntry = useCallback((nextEntry) => {
        const normalized = normalizeRemoteEntry(nextEntry);
        if (!normalized) return;
        setBuilds(prev => {
            const others = prev.filter(entry => entry.id !== normalized.id);
            return [normalized, ...others];
        });
    }, []);

    const patchBuildsEntry = useCallback((id, fn) => {
        if (!id || typeof fn !== 'function') return;
        setBuilds((prev) => prev.map((e) => (e.id === id ? fn(e) : e)));
    }, []);

    const updateCurrentBuildMeta = useCallback((updater) => {
        setCurrentBuildMeta(prev => {
            const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...(updater || {}) };
            currentBuildMetaRef.current = next;
            if (next.scope === 'remote' && activeBuildId) {
                setBuilds(prevBuilds => prevBuilds.map(entry => (
                    entry.id === activeBuildId
                        ? {
                            ...entry,
                            metrics: next.metrics || entry.metrics,
                            viewer: next.viewer || entry.viewer,
                        }
                        : entry
                )));
            }
            return next;
        });
    }, [activeBuildId]);

    const applyEntryState = useCallback((entry, meta) => {
        const loaded = entryToLoadedState(entry);
        setBuildRaw(loaded.build);
        setBuildNameRaw(loaded.buildName);
        setGenConfigRaw(loaded.genConfig);
        setActiveBuildIdState(entry?.id || null);
        setCurrentBuildMeta(meta);
        // Anonymous builds auto-publish. Opening one is the second discrete event
        // worth a push: it carries over edits made in an earlier session and
        // backfills builds that predate the feature. The upsert is idempotent, so
        // re-pushing an unchanged build costs one request and changes nothing.
        if (meta?.scope === 'local' && entry?.id) pushAnonBuild(entry.id);
        if (meta.scope === 'local') {
            buildStorage.setActiveBuildId(entry?.id || null);
        }
        if (meta.scope === 'remote' && entry?.id) {
            localStorage.setItem(KEY_ACTIVE_REMOTE_ID, entry.id);
        }
    }, []);

    const refreshBuilds = useCallback(async () => {
        if (authState === 'loading') return [];
        if (isAuthenticated) {
            try {
                const remoteBuilds = (await listRemoteBuilds()).map(normalizeRemoteEntry).filter(Boolean);
                setBuilds(remoteBuilds);
                setStorageMode('remote');
                return remoteBuilds;
            } catch (error) {
                console.warn('refreshBuilds remote failed:', error);
                setStorageMode('offline-auth-fallback');
                return null;
            }
        }
        const localBuilds = buildStorage.getBuilds();
        setBuilds(localBuilds);
        setStorageMode('local');
        return localBuilds;
    }, [authState, isAuthenticated]);

    /**
     * Copy every build held on this device into the account, and adopt the
     * refreshed remote listing. Returns the server's tally — or null if the
     * import failed, which the caller surfaces.
     *
     * Safe to run more than once: the server fingerprints name + build +
     * genConfig and counts a match as a duplicate rather than saving it again.
     * That is what lets the profile offer this at any time, and not only in the
     * one-shot prompt below.
     */
    const runLocalBuildImport = useCallback(async () => {
        if (!isAuthenticated || !user?.id) return null;
        const localBuilds = buildStorage.getBuilds();
        if (!localBuilds.length) return { imported: 0, duplicates: 0, skipped: 0, builds: null };
        try {
            const result = await importLocalBuilds({ builds: localBuilds });
            // Importing by hand answers the prompt too — otherwise the next
            // bootstrap asks about builds that are already on the account.
            localStorage.setItem(getImportDecisionKey(user.id), 'imported');
            const importedRemote = (await listRemoteBuilds()).map(normalizeRemoteEntry).filter(Boolean);
            setBuilds(importedRemote);
            return {
                imported: result?.imported || 0,
                duplicates: result?.duplicates || 0,
                skipped: result?.skipped || 0,
                builds: importedRemote,
            };
        } catch (error) {
            console.warn('Local build import failed:', error);
            return null;
        }
    }, [isAuthenticated, user?.id]);

    const maybeImportLocalBuilds = useCallback(async (remoteBuilds) => {
        if (!isAuthenticated || !user?.id) return remoteBuilds;
        if (!buildStorage.getBuilds().length) return remoteBuilds;
        const decisionKey = getImportDecisionKey(user.id);
        if (localStorage.getItem(decisionKey)) return remoteBuilds;

        const shouldImport = window.confirm(
            'Import your local builds into this Discord account? Your local copies will stay on this browser.'
        );
        if (!shouldImport) {
            localStorage.setItem(decisionKey, 'skipped');
            return remoteBuilds;
        }
        const result = await runLocalBuildImport();
        return result?.builds || remoteBuilds;
    }, [isAuthenticated, runLocalBuildImport, user?.id]);

    const flushRemotePatch = useCallback(async () => {
        const meta = currentBuildMetaRef.current;
        const payload = pendingRemotePatchRef.current;
        pendingRemotePatchRef.current = {};
        remoteSaveTimerRef.current = null;
        if (meta.scope !== 'remote' || !meta.canEdit || !activeBuildId || !Object.keys(payload).length) {
            return;
        }
        try {
            const updated = await updateRemoteBuild(activeBuildId, payload);
            updateRemoteListEntry(updated);
        } catch (error) {
            console.warn('Remote build save failed:', error);
            setStorageMode('offline-auth-fallback');
        }
    }, [activeBuildId, updateRemoteListEntry]);

    const queueRemotePatch = useCallback((partial) => {
        pendingRemotePatchRef.current = {
            ...pendingRemotePatchRef.current,
            ...partial,
        };
        if (remoteSaveTimerRef.current) {
            clearTimeout(remoteSaveTimerRef.current);
        }
        remoteSaveTimerRef.current = setTimeout(() => {
            flushRemotePatch();
        }, 400);
    }, [flushRemotePatch]);

    const ensureLocalWorkspace = useCallback(() => {
        const localBuilds = buildStorage.getBuilds();
        let entry = null;
        const bootId = buildStorage.consumeBootBuildId();
        if (bootId) {
            entry = buildStorage.getBuild(bootId);
        }
        if (!entry) {
            const activeId = buildStorage.getActiveBuildId();
            if (activeId) {
                entry = buildStorage.getBuild(activeId);
            }
        }
        if (!entry) {
            entry = localBuilds[0] || buildStorage.createBuild();
        }
        setBuilds(buildStorage.getBuilds());
        setStorageMode('local');
        applyEntryState(entry, localBuildMeta(entry));
        return entry;
    }, [applyEntryState]);

    const preApplyRemoteEntry = useCallback((rawEntry) => {
        const entry = normalizeRemoteEntry(rawEntry);
        if (!entry) return false;
        applyEntryState(entry, remoteBuildMeta(entry, {
            canEdit: true,
            ownerSlug: entry.owner?.public_slug || user?.public_slug || null,
        }));
        setStorageMode('remote');
        if (entry.id) {
            localStorage.setItem(KEY_ACTIVE_REMOTE_ID, entry.id);
        }
        return true;
    }, [applyEntryState, user?.public_slug]);

    const openCanonicalBuild = useCallback(async (userSlug, buildCode) => {
        try {
            const resolved = await getPublicBuild(userSlug, buildCode);
            const entry = normalizeRemoteEntry({
                ...(resolved?.build || {}),
                viewer: resolved?.build?.viewer || resolved?.viewer || {},
            });
            if (!entry) return null;
            applyEntryState(entry, remoteBuildMeta(entry, {
                canEdit: !!resolved?.can_edit,
                ownerSlug: entry.owner?.public_slug || userSlug,
            }));
            setStorageMode(resolved?.can_edit ? 'remote' : 'public-readonly');
            return entry;
        } catch (error) {
            console.warn('openCanonicalBuild failed:', error);
            return null;
        }
    }, [applyEntryState]);

    const ensureRemoteWorkspace = useCallback(async () => {
        let remoteBuilds = await refreshBuilds();
        if (remoteBuilds == null) {
            return ensureLocalWorkspace();
        }
        remoteBuilds = await maybeImportLocalBuilds(remoteBuilds);
        if (!Array.isArray(remoteBuilds) || !remoteBuilds.length) {
            const created = await createRemoteBuild({
                name: '',
                build: DEFAULT_REMOTE_BUILD,
                genConfig: getGenDefaults(),
            });
            const entry = normalizeRemoteEntry(created);
            setBuilds(entry ? [entry] : []);
            if (entry) {
                applyEntryState(entry, remoteBuildMeta(entry, {
                    canEdit: true,
                    ownerSlug: entry.owner?.public_slug || user?.public_slug || null,
                }));
                return entry;
            }
            return null;
        }

        const preferredId = localStorage.getItem(KEY_ACTIVE_REMOTE_ID);
        const chosen = remoteBuilds.find(entry => entry.id === preferredId) || remoteBuilds[0];
        if (!chosen) return null;

        // The listing carries card fields only, so the chosen row is not a build
        // yet — fetch it before it becomes the editable workspace. Activating the
        // listing copy would open a build with no equipment and then let the
        // auto-save write that emptiness back over the real one.
        let full = null;
        try {
            full = normalizeRemoteEntry(await getRemoteBuild(chosen.id));
        } catch (error) {
            console.warn('Could not load the active build:', error);
        }
        // Deliberately no fallback to `chosen`: better to start with no remote
        // workspace than to open a truncated one that saves over itself.
        if (!full) return ensureLocalWorkspace();

        applyEntryState(full, remoteBuildMeta(full, {
            canEdit: true,
            ownerSlug: full.owner?.public_slug || user?.public_slug || null,
        }));
        return full;
    }, [applyEntryState, ensureLocalWorkspace, maybeImportLocalBuilds, refreshBuilds, user?.public_slug]);

    const updateBuild = useCallback((newBuildOrUpdater) => {
        const meta = currentBuildMetaRef.current;
        if (meta.scope === 'remote' && !meta.canEdit) return;
        // Resolve updater functions against the latest build (buildRef), and
        // update the ref synchronously so consecutive calls in the same tick
        // compose instead of clobbering each other.
        const newBuild = typeof newBuildOrUpdater === 'function'
            ? newBuildOrUpdater(buildRef.current)
            : newBuildOrUpdater;
        if (newBuild === buildRef.current) return; // updater opted out — no-op
        buildRef.current = newBuild;
        setBuildRaw(newBuild);
        if (meta.scope === 'local') {
            persistLocalEntry(activeBuildId, { build: newBuild });
            return;
        }
        if (meta.scope === 'remote') {
            queueRemotePatch({ build: buildStorage.buildToStorage(newBuild) });
        }
    }, [activeBuildId, persistLocalEntry, queueRemotePatch]);

    const updateBuildName = useCallback((newBuildName) => {
        const meta = currentBuildMetaRef.current;
        if (meta.scope === 'remote' && !meta.canEdit) return;
        setBuildNameRaw(newBuildName);
        if (meta.scope === 'local') {
            persistLocalEntry(activeBuildId, { name: newBuildName });
            return;
        }
        if (meta.scope === 'remote') {
            queueRemotePatch({ name: newBuildName });
        }
    }, [activeBuildId, persistLocalEntry, queueRemotePatch]);

    const updateGenConfig = useCallback((nextGenConfigOrUpdater) => {
        const meta = currentBuildMetaRef.current;
        if (meta.scope === 'remote' && !meta.canEdit) return;
        const resolveNext = (prev) => (
            typeof nextGenConfigOrUpdater === 'function' ? nextGenConfigOrUpdater(prev) : nextGenConfigOrUpdater
        );
        setGenConfigRaw(prev => {
            const next = resolveNext(prev);
            if (meta.scope === 'local') {
                persistLocalEntry(activeBuildId, { genConfig: next });
            } else if (meta.scope === 'remote') {
                queueRemotePatch({
                    genConfig: next && typeof next.toObject === 'function' ? next.toObject() : next,
                });
            }
            return next;
        });
    }, [activeBuildId, persistLocalEntry, queueRemotePatch]);

    const updateActiveMenu = useCallback((menu) => {
        setActiveMenuRaw(menu);
        buildStorage.setActiveMenu(menu);
    }, []);

    const openBuild = useCallback(async (id) => {
        if (!id) return null;
        if (isAuthenticated) {
            const fetched = await getRemoteBuild(id);
            const entry = normalizeRemoteEntry(fetched);
            if (!entry) return null;
            applyEntryState(entry, remoteBuildMeta(entry, {
                canEdit: true,
                ownerSlug: entry.owner?.public_slug || user?.public_slug || null,
            }));
            updateRemoteListEntry(entry);
            return entry;
        }
        const entry = buildStorage.getBuild(id);
        if (!entry) return null;
        applyEntryState(entry, localBuildMeta(entry));
        setBuilds(buildStorage.getBuilds());
        return entry;
    }, [applyEntryState, isAuthenticated, updateRemoteListEntry, user?.public_slug]);

    const createBuild = useCallback(async (source = null, { activate = true } = {}) => {
        if (isAuthenticated) {
            const created = await createRemoteBuild({
                name: source?.name || '',
                build: source?.build || DEFAULT_REMOTE_BUILD,
                genConfig: source?.genConfig || getGenDefaults(),
            });
            const entry = normalizeRemoteEntry(created);
            if (!entry) return null;
            updateRemoteListEntry(entry);
            if (activate) {
                applyEntryState(entry, remoteBuildMeta(entry, {
                    canEdit: true,
                    ownerSlug: entry.owner?.public_slug || user?.public_slug || null,
                }));
            }
            return entry;
        }

        const prevActiveId = buildStorage.getActiveBuildId();
        const created = buildStorage.createBuild();
        if (source) {
            buildStorage.updateBuild(created.id, {
                name: source.name || '',
                build: new Build(source.build || DEFAULT_REMOTE_BUILD),
                genConfig: source.genConfig || {},
            });
        }
        const finalEntry = buildStorage.getBuild(created.id);
        setBuilds(buildStorage.getBuilds());
        if (activate) {
            if (finalEntry) {
                applyEntryState(finalEntry, localBuildMeta(finalEntry));
            }
        } else {
            buildStorage.setActiveBuildId(prevActiveId);
        }
        return finalEntry;
    }, [applyEntryState, isAuthenticated, updateRemoteListEntry, user?.public_slug]);

    const duplicateBuild = useCallback(async (id, options) => {
        let source;
        if (id === activeBuildId) {
            source = {
                id: activeBuildId,
                name: buildName,
                build: buildStorage.buildToStorage(build),
                genConfig: genConfig?.toObject?.() || {},
            };
        } else if (isAuthenticated) {
            // Remote listings are card-only; copying one would duplicate a build
            // with no equipment. Local entries in `builds` are whole, so only the
            // remote case needs the round trip.
            source = normalizeRemoteEntry(await getRemoteBuild(id));
        } else {
            source = builds.find(entry => entry.id === id) || null;
        }
        if (!source) return null;
        return createBuild({
            name: buildStorage.duplicateName(source.name),
            build: source.build,
            genConfig: source.genConfig || {},
        }, options);
    }, [activeBuildId, build, buildName, builds, createBuild, genConfig, isAuthenticated]);

    const deleteBuild = useCallback(async (id) => {
        if (!id) return false;
        if (isAuthenticated) {
            await deleteRemoteBuild(id);
            setBuilds(prev => prev.filter(entry => entry.id !== id));
            if (activeBuildId === id) {
                setActiveBuildIdState(null);
            }
            return true;
        }
        buildStorage.deleteBuild(id);
        setBuilds(buildStorage.getBuilds());
        if (activeBuildId === id) {
            ensureLocalWorkspace();
        }
        return true;
    }, [activeBuildId, ensureLocalWorkspace, isAuthenticated]);

    const clearSavedData = useCallback(() => {
        try {
            // Retract first: the public codes needed to unpublish live in the
            // storage about to be wiped, so clearing without this would strand
            // the published copies with no way to remove them.
            retractAllAnonBuilds(buildStorage.getBuilds());
            localStorage.removeItem('dofgen_builds');
            localStorage.removeItem('dofgen_active_build_id');
            ensureLocalWorkspace();
        } catch (error) {
            console.warn('clearSavedData failed:', error);
        }
    }, [ensureLocalWorkspace]);

    const loadSavedData = useCallback(async () => {
        if (isAuthenticated) {
            await ensureRemoteWorkspace();
            return;
        }
        ensureLocalWorkspace();
    }, [ensureLocalWorkspace, ensureRemoteWorkspace, isAuthenticated]);

    const exportState = useCallback(() => {
        try {
            return JSON.stringify({
                build: build ? buildStorage.buildToStorage(build) : null,
                buildName,
                genConfig: genConfig ? genConfig.toObject() : null,
                activeMenu,
                timestamp: new Date().toISOString(),
            }, null, 2);
        } catch (error) {
            console.warn('exportState failed:', error);
            return null;
        }
    }, [activeMenu, build, buildName, genConfig]);

    const importState = useCallback(async (jsonData) => {
        try {
            const stateData = JSON.parse(jsonData);
            const imported = {
                name: stateData.buildName || '',
                build: stateData.build || DEFAULT_REMOTE_BUILD,
                genConfig: stateData.genConfig || {},
            };
            if (isAuthenticated) {
                await createBuild(imported);
                if (stateData.activeMenu) updateActiveMenu(stateData.activeMenu);
                return true;
            }

            let activeId = buildStorage.getActiveBuildId();
            if (!activeId) {
                activeId = buildStorage.createBuild().id;
            }
            buildStorage.updateBuild(activeId, {
                name: imported.name,
                build: new Build(imported.build),
                genConfig: imported.genConfig,
            });
            const entry = buildStorage.getBuild(activeId);
            if (entry) {
                applyEntryState(entry, localBuildMeta(entry));
            }
            setBuilds(buildStorage.getBuilds());
            if (stateData.activeMenu) updateActiveMenu(stateData.activeMenu);
            return true;
        } catch (error) {
            console.warn('importState failed:', error);
            return false;
        }
    }, [applyEntryState, createBuild, isAuthenticated, updateActiveMenu]);

    const routeBootstrap = useCallback(async ({ userSlug = null, buildCode = null } = {}) => {
        // The catalog is a precondition, not an optimisation. A saved build is
        // sixteen item *ids* and a set of piece counts; without the catalog to
        // resolve them `getStats` sees no items and no set bonuses, so the build
        // renders with a stat sheet that is quietly wrong rather than obviously
        // empty. `isBootstrapping` covers the wait with a skeleton; costs
        // nothing once warm, because `loadItemCatalog` returns the cached copy.
        //
        // It resolves to null rather than throwing when the catalog cannot be
        // had at all. The skeleton must still come down in that case — the app
        // degrades to what it did before the catalog existed, which is worse
        // than a build but far better than a page that never loads. Hence the
        // `finally`, which also covers a failed build fetch.
        try {
            await loadItemCatalog();
            if (userSlug && buildCode) {
                const opened = await openCanonicalBuild(userSlug, buildCode);
                if (opened) return opened;
                return null;
            }
            if (isAuthenticated) {
                return await ensureRemoteWorkspace();
            }
            return await ensureLocalWorkspace();
        } finally {
            setIsBootstrapping(false);
        }
    }, [ensureLocalWorkspace, ensureRemoteWorkspace, isAuthenticated, openCanonicalBuild]);

    const getBuildPath = useCallback((entry, lang, dataTab = null) => {
        if (!entry) return null;
        if (entry.publicCode && entry.owner?.public_slug) {
            return canonicalBuildPath(lang, entry.owner.public_slug, entry.publicCode, dataTab);
        }
        return null;
    }, []);

    const getActiveBuildPath = useCallback((lang, dataTab = null) => {
        if (currentBuildMetaRef.current.scope !== 'remote') return null;
        const ownerSlug = currentBuildMetaRef.current.ownerSlug;
        const publicCode = currentBuildMetaRef.current.publicCode;
        if (!ownerSlug || !publicCode) return null;
        return canonicalBuildPath(lang, ownerSlug, publicCode, dataTab);
    }, []);

    useEffect(() => {
        if (authState === 'loading') return;
        if (
            !isAuthenticated
            && currentBuildMetaRef.current.scope === 'remote'
            && currentBuildMetaRef.current.canEdit
        ) {
            ensureLocalWorkspace();
        }
        if (!isAuthenticated) {
            setStorageMode('local');
        }
    }, [authState, ensureLocalWorkspace, isAuthenticated]);

    useEffect(() => () => {
        if (remoteSaveTimerRef.current) {
            clearTimeout(remoteSaveTimerRef.current);
        }
    }, []);

    const value = useMemo(() => ({
        build,
        buildName,
        genConfig,
        activeMenu,
        builds,
        activeBuildId,
        storageMode,
        isBootstrapping,
        currentBuildMeta,
        updateCurrentBuildMeta,
        isReadOnly: currentBuildMeta.scope === 'remote' && !currentBuildMeta.canEdit,
        isRemoteBuild: currentBuildMeta.scope === 'remote',
        isAuthenticated,
        setBuild: updateBuild,
        setBuildName: updateBuildName,
        setGenConfig: updateGenConfig,
        setActiveMenu: updateActiveMenu,
        openBuild,
        openCanonicalBuild,
        preApplyRemoteEntry,
        createBuild,
        deleteBuild,
        duplicateBuild,
        refreshBuilds,
        runLocalBuildImport,
        patchBuildsEntry,
        routeBootstrap,
        clearSavedData,
        loadSavedData,
        exportState,
        importState,
        getBuildPath,
        getActiveBuildPath,
    }), [
        activeBuildId,
        activeMenu,
        build,
        buildName,
        builds,
        createBuild,
        currentBuildMeta,
        deleteBuild,
        duplicateBuild,
        exportState,
        genConfig,
        getActiveBuildPath,
        getBuildPath,
        importState,
        isBootstrapping,
        loadSavedData,
        openBuild,
        openCanonicalBuild,
        preApplyRemoteEntry,
        patchBuildsEntry,
        refreshBuilds,
        routeBootstrap,
        runLocalBuildImport,
        storageMode,
        updateActiveMenu,
        updateBuild,
        updateBuildName,
        updateCurrentBuildMeta,
        updateGenConfig,
        clearSavedData,
        isAuthenticated,
    ]);

    return value;
};

export default useGeneratorState;
